import { sizes as themeSizes } from '@frontend/components/src/theme/foundations/sizes';
import {
  getBaseStyleObjectFromStyleConfig,
  getPartStyleFromStyleInterpolation,
  getStyleObjectFromStyleInterpolation,
  type StyleObjectMulti,
  type StyleFunctionProps,
} from '@frontend/components/src/theme/styleConfigHelpers';

import { CLOSE_BUTTON_STYLE_CONFIG } from '../closeButton/closeButtonStyleConfig';
import {
  INPUT_SIZE_TO_SIZING_DATA_MAP,
  INPUT_STYLE_CONFIG,
} from '../input/inputStyleConfig';
import { CASCADER_PARTS } from './consts';
import type {
  CascaderColorScheme,
  CascaderPart,
  CascaderSize,
  CascaderStyleConfig,
  CascaderVariant,
} from './types';

export const cascaderMenuItemStyles = {
  minWidth: 15,
  maxWidth: 50,
  minHeight: 3.5,
  py: 0.5,
  pl: 2,
  pr: 1,
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  gap: 0.5,
  flexShrink: 0,
  _hover: {
    bg: 'interaction.surfaceHover',
  },
  '&[data-highlighted]': {
    bg: 'interaction.surfaceActive',
  },
};

const baseStyle: CascaderStyleConfig['baseStyle'] = ({ theme, colorMode }) => {
  const closeButtonStyles = getBaseStyleObjectFromStyleConfig(
    CLOSE_BUTTON_STYLE_CONFIG,
    { theme, colorMode, colorScheme: 'default' },
  );

  const inputFieldStyles = getPartStyleFromStyleInterpolation(
    INPUT_STYLE_CONFIG.baseStyle,
    'field',
    { theme, colorMode },
  );

  const messageStyles = {
    h: 5,
    px: 2,
    mb: -1, // to accommodate for necessary pb: 1 on menu list
    color: 'feature.placeholder',
    fontSize: 'md',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const wrapperStyles = {
    display: 'flex',
    bg: 'page.surface',
    border: '1px solid',
    borderColor: 'separator.border',
    borderRadius: 'md',
    shadow: 'popover',
    zIndex: 'popover',
    minWidth: 'max-content',
  };

  const searchStyles = {
    searchWrapper: {
      ...wrapperStyles,
      flexDirection: 'column',
      minWidth: 15,
    },
    searchOptionsList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      overflowY: 'auto',
    },
    searchOption: {
      cursor: 'pointer',
      position: 'relative',
      h: 6,
      py: 1.5,
      px: 1,
      display: 'flex',
      flexDirection: 'column',
      _notLast: {
        borderBottomStyle: 'solid',
        borderBottom: '1px solid',
        borderColor: 'separator.divider',
      },
      wordBreak: 'break-all',
      '&[data-highlighted]': {
        bg: 'interaction.surfaceActive',
      },
      minWidth: 15,

      minHeight: 3.5,
      pl: 2,
      pr: 1,
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      flexShrink: 0,
    },
  };

  const cascaderMenuStyles = {
    leafOption: {
      ...cascaderMenuItemStyles,
    },
    submenuOption: {
      ...cascaderMenuItemStyles,
    },
    menuWrapper: {
      ...wrapperStyles,
      flexDirection: 'row',
      minWidth: 'var(--reference-width)',
      width: `max-content`,
      maxWidth: '800px',
      overflowX: 'scroll',
      scrollbarWidth: 'none',
    },
    submenuWrapper: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 15,
      maxWidth: 50,
      maxHeight: 40,
      alignItems: 'stretch',
      flexShrink: 0,
      gap: 0,
      _notFirst: {
        borderLeftStyle: 'solid',
        borderLeftWidth: 1,
        borderLeftColor: 'separator.border',
      },
      _last: {
        borderRightStyle: 'solid',
        borderRightWidth: 1,
        borderRightColor: 'separator.border',
      },
      overflow: 'auto',
      '&:has(.cascader-submenu-header)': {
        scrollPaddingTop: 3.5,
      },
    },
    submenuHeader: {
      flexShrink: 0,
      minHeight: 3.5,
      pt: 1,
      pb: 0.5,
      px: 2,
      bg: 'page.surface',
      position: 'sticky',
      top: 0,
      gap: 0.125,
    },
    submenuGroupWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      alignItems: 'stretch',
      margin: 0,
      overflow: 'clip',
      paddingBottom: 0.5,
    },
    submenuGroupHeader: {
      lineHeight: 2,
    },
    submenuGroupItemList: {
      listStyle: 'none',
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      alignItems: 'stretch',
    },
    submenuGroupDivider: {
      my: 0.5,
    },
  };

  const inputControlStyles = {
    input: {
      p: 0,
      overflow: 'hidden',
      flexShrink: 1,
      flexGrow: 1,
      border: 'none',
      position: 'relative',
      zIndex: 1,
      _focus: {
        border: 'none',
        outline: 'none',
      },
      _focusVisible: {
        outline: 'none',
        border: 'none',
      },
    },
    clearIndicator: {
      ...closeButtonStyles,
      p: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'feature.secondary',
    },
    control: {
      zIndex: 1,
      display: 'flex',
      alignItems: 'stretch',
      ...inputFieldStyles,
      p: 0,
      overflow: 'hidden',
      w: '100%',
    },
    dropdownIndicator: {
      borderRadius: 0,
      borderWidth: 0,
      cursor: 'pointer',
      color: 'feature.secondary',
    },
    indicatorsContainer: {
      zIndex: 2,
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      alignSelf: 'stretch',
    },
    singleSelectedValue: {
      color: 'feature.primary',
      mx: 0.25,
      maxWidth: 'calc(100% - 8px)',
      overflow: 'hidden',
      position: 'absolute',
      zIndex: 3,
      pointerEvents: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    selectedValueContainer: {
      zIndex: 1,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      paddingX: 1,
      flex: 1,
      flexWrap: 'no-wrap',
      overflow: 'hidden',
      minWidth: 0,
    },
    placeholder: {
      color: 'feature.placeholder',
      position: 'absolute',
      zIndex: 3,
      top: '50%',
      transform: 'translateY(-50%)',
      boxSizing: 'border-box',
      pointerEvents: 'none',
      left: 1,
      right: 0,
    },
    indicatorSeparator: {
      opacity: 1,
      h: '65%',
    },
  };

  return {
    container: {
      minWidth: 0,
    },
    positioningContainer: {},
    noOptionsMessage: {
      ...messageStyles,
      h: 3,
      mb: 0,
    },
    ...searchStyles,
    ...cascaderMenuStyles,
    ...inputControlStyles,
  };
};

const getBaseSizeStyles = (
  size: CascaderSize,
  {
    theme,
    colorMode,
  }: StyleFunctionProps<CascaderVariant, CascaderColorScheme>,
): StyleObjectMulti<CascaderPart> => {
  const inputSizingData = INPUT_SIZE_TO_SIZING_DATA_MAP[size];
  const inputHeight = inputSizingData.h;

  const inputFieldStyles = getPartStyleFromStyleInterpolation(
    INPUT_STYLE_CONFIG.sizes[size],
    'field',
    { theme, colorMode },
  );

  const closeButtonStyles = getStyleObjectFromStyleInterpolation(
    CLOSE_BUTTON_STYLE_CONFIG.sizes[size],
    { theme, colorMode, colorScheme: 'default' },
  );

  return {
    control: {
      ...inputFieldStyles,
      px: 0,
      minH: inputHeight,
      h: 'auto',
    },
    clearIndicator: {
      ...closeButtonStyles,
      mx: inputSizingData.px / 2,
    },
    dropdownIndicator: {
      w: inputHeight,
      maxH: `calc(${themeSizes[inputHeight]} - (1px * 2))`,
    },
  };
};

const SIZES: CascaderStyleConfig['sizes'] = {
  lg: (styleFunctionProps) => {
    const base = getBaseSizeStyles('lg', styleFunctionProps);

    return {
      ...base,
      dropdownIndicator: {
        ...base.dropdownIndicator,
        fontSize: '2.5rem',
      },
    };
  },
  md: (styleFunctionProps) => {
    const base = getBaseSizeStyles('md', styleFunctionProps);

    return {
      ...base,
      dropdownIndicator: {
        ...base.dropdownIndicator,
        fontSize: '2rem',
      },
    };
  },
  sm: (styleFunctionProps) => {
    const base = getBaseSizeStyles('sm', styleFunctionProps);

    return {
      ...base,
      dropdownIndicator: {
        ...base.dropdownIndicator,
        fontSize: '1.5rem',
      },
    };
  },
};

const DEFAULT_PROPS = {
  size: 'md',
} as const;

export const CASCADER_STYLE_CONFIG: CascaderStyleConfig = {
  parts: CASCADER_PARTS,
  baseStyle,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
